"use client";

import { clsx } from "clsx";
import { useState } from "react";
import NextLink from "next/link";

export type ChapterItem = {
  sectionId: string;
  id: string;
  label: string;
};
export type ChapterSelectorProperties = {
  items: ChapterItem[];
  firstSectionId: string | null;
  lastSectionId: string | null;
  theme?: "light" | "dark";
};
export const ChapterSelector = ({
  items,
  firstSectionId,
  lastSectionId,
  theme,
}: ChapterSelectorProperties) => {
  const [activeSectionId, setActiveSectionId] = useState(firstSectionId);
  const isLastActive = activeSectionId === lastSectionId;
  const isDark = theme === "dark";

  return (
    <div className="container mx-auto mt-4 w-auto max-md:px-4">
      <div className="relative">
        <div className="overflow-x-auto p-2">
          <nav
            className="flex min-w-max gap-2 bg-light-gray data-[is-dark=true]:bg-core-blue"
            data-is-dark={isDark}
          >
            {items.map((chapter) => {
              const isActive = activeSectionId === chapter.sectionId;
              return (
                <NextLink
                  className={clsx(
                    "h-22 w-33 shrink-0 cursor-pointer p-1 text-s-body focus-within:outline-2 focus-within:outline-offset-3 focus-within:outline-black focus-visible:shadow-[0_0_0_3px_white] md:size-auto md:flex-1 md:shrink",
                    isDark
                      ? {
                          "bg-core-blue text-white focus-within:bg-white/85 focus-within:text-royal-blue hover:bg-white/85 hover:text-royal-blue":
                            !isActive,
                          "bg-off-white text-core-blue": isActive,
                        }
                      : {
                          "bg-light-gray text-core-blue focus-within:bg-core-blue focus-within:text-white hover:bg-core-blue hover:text-white":
                            !isActive,
                          "bg-royal-blue text-white": isActive,
                        },
                  )}
                  href={`#${chapter.sectionId}`}
                  key={chapter.id}
                  onClick={() => {
                    setActiveSectionId(chapter.sectionId);
                  }}
                  onFocus={(event) => {
                    event.target.scrollIntoView({
                      behavior: "smooth",
                      block: "nearest",
                      inline: "center",
                    });
                  }}
                >
                  {chapter.label}
                </NextLink>
              );
            })}
          </nav>
        </div>

        <div
          className={clsx(
            "pointer-events-none absolute inset-y-2 end-0 w-36 bg-linear-to-l from-light-gray to-transparent data-[is-dark=true]:from-core-blue lg:hidden",
            { hidden: isLastActive },
          )}
          data-is-dark={isDark}
        />
      </div>
    </div>
  );
};
